import useEventsDatabase from "~/composables/supabase/useEventsDatabase";
import useEventsFightCardsDatabase from "~/composables/supabase/useEventsFightCardsDatabase";
import { type EventFighter } from "~/types/events.types";
import { MartialArts, Sex } from "~/types/enums";
import {
  EventsDateFilter,
  type EventsFilter,
  type EventUIModel,
} from "~/types/events.types";
import type { PlayerUIModel } from "~/types/players.types";
import type { Tables } from "~/types/types.gen";
import useEventsFightersDatabase from "~/composables/supabase/useEventsFightersDatabase";

export const useEventsStore = defineStore("eventStore", {
  state: () => ({
    eventFightersByEventId: {} as Record<string, EventFighter[] | null>,
    eventFightersLoadingByEventId: {} as Record<string, boolean>,
    eventFightersLastFetchByEventId: {} as Record<string, number>,

    fightCardsByEventId: {} as Record<
      string,
      Tables<"event_fight_cards">[] | null
    >,
    fightCardsLoadingByEventId: {} as Record<string, boolean>,

    eventsFilter: {
      dateFilter: EventsDateFilter.UPCOMING,
      searchQuery: "",
    } as EventsFilter,
    page: 0,
    pageCount: 20,
    eventCategories: [] as Tables<"event_categories">[],
    // TODO: Move this to environment variables or a config file
    defaultEventCoverPhotoUrl:
      "https://f003.backblazeb2.com/file/fighter-registry-stg/events/placeholder_image.webp",
  }),
  getters: {
    canUserEditEvent: (state) => {
      const userStore = useUserStore();
      const orgStore = useOrganisationStore();
      const gymStore = useGymStore();

      return (eventData: EventUIModel | null | undefined): boolean => {
        // user can edit if they're a member of the organization/gym that created the event
        if (!eventData) return false;

        if (!userStore.isUserLoggedIn) {
          return false;
        }

        const organisationId =
          eventData.created_by_org ?? eventData.author_org?.id;
        if (organisationId) {
          return orgStore.isUserMemberOfOrganisation(organisationId);
        }

        const gymId = eventData.created_by_gym ?? eventData.author_gym?.id;
        if (gymId) {
          return gymStore.isUserMemberOfGym(gymId);
        }

        return false;
      };
    },

    isEventInProgress: () => (event: EventUIModel | null | undefined) => {
      if (!event || !event.datetime_start) return false;
      const now = new Date();
      const start = new Date(event.datetime_start);
      const end = event.datetime_end ? new Date(event.datetime_end) : null;
      return start <= now && (!end || end >= now);
    },

    isEventInPast: () => (event: EventUIModel | null | undefined) => {
      if (!event || !event.datetime_end) return false;
      const now = new Date();
      const end = new Date(event.datetime_end);
      return end < now;
    },

    getEventCategoryById: (state) => (categoryId: string) => {
      return state.eventCategories.find((item) => item.id == categoryId);
    },
    getEventCategoriesByIds: (state) => (categoryIds: string[]) => {
      return state.eventCategories.filter((item) =>
        categoryIds.includes(item.id),
      );
    },
    eventCategoryNames: (state) => (gender: Sex) => {
      const categories = [
        ...new Set(
          state.eventCategories
            .filter((item) => item.gender === gender)
            .map((item) => item.martial_art),
        ),
      ];
      return categories.sort();
    },
    getEventCategoriesForMartialArt:
      (state) => (martialArt: string, gender: Sex) => {
        // Return all categories for the given martial art and gender, sorted by weight_from.
        // If weight_from is null, treat it as -Infinity so it appears at the start.
        // If weight_to is null, treat it as -Infinity when weight_from matches.
        return state.eventCategories
          .filter(
            (item) => item.martial_art == martialArt && item.gender === gender,
          )
          .sort((a, b) => {
            const weightFromA = a.weight_from ?? -Infinity;
            const weightFromB = b.weight_from ?? -Infinity;

            if (weightFromA === weightFromB) {
              const weightToA = a.weight_to ?? -Infinity;
              const weightToB = b.weight_to ?? -Infinity;
              return weightToA - weightToB;
            }

            return weightFromA - weightFromB;
          });
      },
    getEventTypeForCategoryIds: (state) => (categoryIds: string[]) => {
      const allTypes = categoryIds.map((categoryId) => {
        return state.eventCategories.find((item) => item.id == categoryId)
          ?.type;
      });

      const eventTypes = [...new Set(allTypes)];
      return eventTypes.filter((type) => type != null);
    },
    getEventTypeForCategoryIdsTranslated:
      (state) => (categoryIds: string[] | null) => {
        if (categoryIds == null) {
          return [];
        }

        const { $i18n } = useNuxtApp();
        const { t } = $i18n;

        const eventTypes =
          useEventsStore().getEventTypeForCategoryIds(categoryIds) || [];

        return eventTypes
          .map((item) => {
            return t("enum.open_to_fight_category." + item?.toLowerCase());
          })
          .join(", ");
      },
    getEventMartialArtForCategoryIds:
      (state) =>
      (
        categoryIds: string[] | null | undefined,
        translated: Boolean = true,
      ) => {
        if (!categoryIds) {
          return [];
        }

        const categories = categoryIds
          .map((categoryId) => {
            return state.eventCategories.find((item) => item.id == categoryId);
          })
          .filter(
            (category): category is Tables<"event_categories"> => !!category,
          );

        const martialArts = [
          ...new Set(
            categories
              .map((category) => category.martial_art)
              .filter((martialArt): martialArt is MartialArts => !!martialArt),
          ),
        ];

        if (!translated) {
          return martialArts;
        }

        const { $i18n } = useNuxtApp();
        const { t } = $i18n;
        const translatedMartialArts = new Set<string>();

        categories.forEach((category) => {
          const martialArt = category.martial_art;
          if (!martialArt) {
            return;
          }

          translatedMartialArts.add(
            t(`enum.martial_art.${martialArt.toLowerCase()}`),
          );
        });

        return Array.from(translatedMartialArts)
          .sort((a, b) => a.localeCompare(b))
          .join(", ");
      },
    eventCategoryIdListToMartialArtMap:
      (state) => (categoryIds: string[] | null | undefined) => {
        if (!categoryIds) {
          return new Map<MartialArts, string[]>();
        }

        const arrayOfMartialArts =
          useEventsStore().getEventMartialArtForCategoryIds(
            categoryIds,
            false,
          ) as MartialArts[];

        const map = new Map<MartialArts, string[]>();

        // Initialize map with empty arrays for each martial art
        arrayOfMartialArts.forEach((martialArt) => {
          if (martialArt) {
            map.set(martialArt as MartialArts, []);
          }
        });

        // Populate the map with category IDs for each martial art
        categoryIds.forEach((categoryId) => {
          const category = state.eventCategories.find(
            (item) => item.id === categoryId,
          );
          if (category && category.martial_art) {
            const existingIds =
              map.get(category.martial_art as MartialArts) || [];
            map.set(category.martial_art as MartialArts, [
              ...existingIds,
              categoryId,
            ]);
          }
        });

        return map;
      },

    sortFightersPerEventCategory:
      (state) =>
      (
        fighters: (Tables<"players"> | EventFighter)[],
        isEventFighterObjects: boolean = false,
        filterOpenCategories: boolean = false,
      ) => {
        const categories: {
          category: Tables<"event_categories">;
          fighters: (Tables<"players"> | EventFighter)[];
        }[] = [];

        if (isEventFighterObjects) {
          useEventsStore().eventCategories.forEach((category) => {
            const matchedFighters = (fighters as EventFighter[]).filter(
              (fighter) => fighter.event_categories.includes(category.id),
            );

            if (matchedFighters.length > 0) {
              categories.push({
                category,
                fighters: matchedFighters,
              });
            }
          });
        } else {
          fighters.forEach((fighterItem) => {
            const fighter = fighterItem as Tables<"players">;
            const fighterWithMatches = fighterItem as PlayerUIModel;

            let matchedCategories = fighterWithMatches.matched_categories ?? [];

            // Filter out open categories
            if (filterOpenCategories) {
              matchedCategories = matchedCategories.filter(
                (cat) => cat.name?.toLowerCase() !== "open class",
              );
            }

            if (matchedCategories.length == 0) {
              return;
            }

            const matchedCategory = matchedCategories[0];

            const index = categories.findIndex(
              (cat) => cat.category.id == matchedCategory.id,
            );

            if (index != -1) {
              categories[index].fighters.push(fighter);
            } else {
              categories.push({
                category: matchedCategory,
                fighters: [fighter],
              });
            }
          });
        }

        // Sort alphabetically by category.martial_art and then by category.weight_from
        categories.sort((a, b) => {
          if (a.category.martial_art! < b.category.martial_art!) return -1;
          if (a.category.martial_art! > b.category.martial_art!) return 1;
          if (a.category.weight_from! < b.category.weight_from!) return -1;
          if (a.category.weight_from! > b.category.weight_from!) return 1;
          return 0;
        });

        return categories;
      },

    getEventCategoryTitleAndSubtitle:
      (state) => (categoryId: string | null | undefined) => {
        if (!categoryId) {
          return {
            title: "",
            subtitle: "",
          };
        }

        const category = state.eventCategories.find(
          (cat) => cat.id === categoryId,
        );

        if (!category) {
          return {
            title: "",
            subtitle: "",
          };
        }

        const { $i18n } = useNuxtApp();
        const { t } = $i18n;

        const categoryMartialArt = t(
          "enum.martial_art." + category.martial_art?.toLowerCase(),
        );

        const categoryTypeOrGender = category.type
          ? t("enum.open_to_fight_category." + category.type?.toLowerCase())
          : t("enum.gender." + category.gender?.toLowerCase());

        const categoryTitle = `${categoryMartialArt} - ${
          category.name ?? ""
        } - ${categoryTypeOrGender}`;

        const categorySubtitle = `${category.weight_from ?? "∞"} kg - ${
          category.weight_to ?? "∞"
        } kg`;

        return {
          title: categoryTitle,
          subtitle: categorySubtitle,
        };
      },

    getEventFighterMatchRecordString: () => (eventFighter: EventFighter) => {
      // if event has started get snapshot from event fighter data
      // else get current record from player store
      if (eventFighter && eventFighter.fighter_snapshot) {
        return {
          amateur: `${eventFighter.fighter_snapshot.win_count}W - ${eventFighter.fighter_snapshot.draw_count}D - ${eventFighter.fighter_snapshot.lose_count}L`,
          amateur_total: eventFighter.fighter_snapshot.total_match_count,
          professional: `${eventFighter.fighter_snapshot.win_count_pro}W - ${eventFighter.fighter_snapshot.draw_count_pro}D - ${eventFighter.fighter_snapshot.lose_count_pro}L`,
          professional_total:
            eventFighter.fighter_snapshot.total_match_count_pro,
        };
      }

      if (!eventFighter || !eventFighter.player) {
        return {
          amateur: `0W - 0D - 0L`,
          amateur_total: 0,
          professional: `0W - 0D - 0L`,
          professional_total: 0,
        };
      }

      return {
        amateur: `${eventFighter.player.win_count}W - ${eventFighter.player.draw_count}D - ${eventFighter.player.lose_count}L`,
        amateur_total: eventFighter.player.total_match_count,
        professional: `${eventFighter.player.win_count_pro}W - ${eventFighter.player.draw_count_pro}D - ${eventFighter.player.lose_count_pro}L`,
        professional_total: eventFighter.player.total_match_count_pro,
      };
    },
  },

  actions: {
    ///////////////////////////////////////////////////////////////////////////////////
    // Event Methods
    ///////////////////////////////////////////////////////////////////////////////////
    async getManageEventById(
      eventId: string | undefined,
    ): Promise<EventUIModel | null> {
      if (!eventId) {
        return null;
      }

      return await useEventsDatabase().fetchManageEvent(eventId);
    },

    async addEvent(data: Tables<"events">) {
      await useEventsDatabase().insertEvent(data);
    },

    async updateEvent(data: Partial<Tables<"events">>) {
      await useEventsDatabase().updateEvent(data);
    },

    async removeEvent(eventData: EventUIModel) {
      await useEventsDatabase().removeEvent(eventData);
      this.eventFightersByEventId[eventData.id] = null;
      this.fightCardsByEventId[eventData.id] = null;
    },

    ///////////////////////////////////////////////////////////////////////////////////
    // Event Fighter Methods
    ///////////////////////////////////////////////////////////////////////////////////
    async fetchEventFightersForEvent(
      eventId: string | undefined,
    ): Promise<EventFighter[]> {
      if (!eventId) {
        return [];
      }

      const now = Date.now();
      const lastFetch = this.eventFightersLastFetchByEventId[eventId];
      const oneMinuteInMs = 60 * 1000;

      // Check if we have cached data and it's still valid (less than 1 minute old)
      if (
        this.eventFightersByEventId[eventId] &&
        lastFetch &&
        now - lastFetch < oneMinuteInMs
      ) {
        return this.eventFightersByEventId[eventId];
      }

      this.eventFightersLoadingByEventId[eventId] = true;

      const data =
        await useEventsFightersDatabase().fetchEventFighters(eventId);

      this.eventFightersLoadingByEventId[eventId] = false;
      this.eventFightersByEventId[eventId] = data;
      this.eventFightersLastFetchByEventId[eventId] = now;

      return this.eventFightersByEventId[eventId] ?? [];
    },

    async addEventFighters(
      eventId: string,
      newData: Tables<"events_fighters">[],
    ) {
      const data = await useEventsFightersDatabase().insert(newData);

      // Append new fighters to existing list
      const existingFighters = this.eventFightersByEventId[eventId] ?? [];
      this.eventFightersByEventId[eventId] = [...existingFighters, ...data];
    },

    async updateEventFighters(
      eventId: string,
      newData: Partial<Tables<"events_fighters">>[],
    ) {
      const data = await useEventsFightersDatabase().update(newData);

      data.forEach((element) => {
        const arr = this.eventFightersByEventId[eventId] ?? [];
        const idx = arr.findIndex((c) => c.id === element.id);
        if (idx >= 0) arr[idx] = element;
      });
    },

    async removeEventFighter(eventId: string, eventRegistrationId: string) {
      const data = await useEventsFightersDatabase().remove(
        eventId,
        eventRegistrationId,
      );

      // Remove the registration from the event registration list
      const arr = this.eventFightersByEventId[eventId] ?? [];
      const idx = arr.findIndex((el) => el.id === eventRegistrationId);
      if (idx >= 0) {
        arr.splice(idx, 1);
      }

      // Update the modified fight card in the cache
      if (data.modifiedFightCardData && this.fightCardsByEventId[eventId]) {
        const fightCardIdx = this.fightCardsByEventId[eventId]!.findIndex(
          (card) => card.id === data.modifiedFightCardData!.id,
        );
        if (fightCardIdx !== -1) {
          this.fightCardsByEventId[eventId]![fightCardIdx] =
            data.modifiedFightCardData;
        }
      }

      // Remove the associated fight cards from the cache
      if (data.removedFightCardId && this.fightCardsByEventId[eventId]) {
        this.fightCardsByEventId[eventId] = this.fightCardsByEventId[
          eventId
        ]!.filter((card) => card.id !== data.removedFightCardId);
      }
    },

    ///////////////////////////////////////////////////////////////////////////////////
    // Event Fight Card Methods
    ///////////////////////////////////////////////////////////////////////////////////
    async fetchFightCardsForEvent(
      eventId: string,
    ): Promise<Tables<"event_fight_cards">[]> {
      if (this.fightCardsByEventId[eventId]) {
        return this.fightCardsByEventId[eventId]!;
      }

      this.fightCardsLoadingByEventId[eventId] = true;

      const { data, error } =
        await useEventsFightCardsDatabase().fetchFightCards(eventId);

      this.fightCardsLoadingByEventId[eventId] = false;

      if (error) throw error;

      this.fightCardsByEventId[eventId] = data;

      return data;
    },

    async addFightCard(card: Tables<"event_fight_cards">) {
      const { data, error } =
        await useEventsFightCardsDatabase().createFightCard(card);

      if (error) throw error;

      if (data && data.event_id) {
        if (!this.fightCardsByEventId[data.event_id]) {
          this.fightCardsByEventId[data.event_id] = [];
        }

        this.fightCardsByEventId[data.event_id]?.push(data);
      }
    },

    async updateFightCard(card: Partial<Tables<"event_fight_cards">>) {
      const { data, error } =
        await useEventsFightCardsDatabase().updateFightCard(card);

      if (error) throw error;

      if (data && card.event_id) {
        const arr = this.fightCardsByEventId[card.event_id] ?? [];
        const idx = arr.findIndex((c) => c.id === card.id);
        if (idx >= 0) arr[idx] = data;
      }

      // Invalidate player cache for both fighters so their records get updated
      usePlayerStore().invalidatePlayerCacheById(card.fighter_a_id);
      usePlayerStore().invalidatePlayerCacheById(card.fighter_b_id);
    },

    async removeFightCard(eventId: string, id: string) {
      const { error } = await useEventsFightCardsDatabase().deleteFightCard(id);

      if (error) throw error;

      this.fightCardsByEventId[eventId] = (
        this.fightCardsByEventId[eventId] ?? []
      ).filter((c) => c.id !== id);
    },

    async autoGenerateFightCard(eventId: string): Promise<number> {
      const newFightCards =
        await useEventsFightCardsDatabase().autoGenerateFightCard(eventId);

      if (newFightCards.length > 0) {
        if (!this.fightCardsByEventId[eventId]) {
          this.fightCardsByEventId[eventId] = [];
        }

        this.fightCardsByEventId[eventId]?.push(...newFightCards);

        return newFightCards.length;
      }

      return 0;
    },

    async reorderFightCards(fightCards: Tables<"event_fight_cards">[]) {
      const { data, error } =
        await useEventsFightCardsDatabase().upsertFightCards(fightCards);

      if (error) throw error;

      if (data && data.length > 0) {
        const eventId = data[0].event_id;
        if (eventId && this.fightCardsByEventId[eventId]) {
          data.forEach((updatedCard) => {
            const index = this.fightCardsByEventId[eventId]!.findIndex(
              (c) => c.id === updatedCard.id,
            );
            if (index !== -1) {
              this.fightCardsByEventId[eventId]![index] = updatedCard;
            }
          });
        }
      }
    },

    ///////////////////////////////////////////////////////////////////////////////////
    // Event Category Methods
    ///////////////////////////////////////////////////////////////////////////////////
    async fetchEventCategories() {
      if (this.eventCategories.length > 0) {
        return;
      }

      this.eventCategories =
        (await useEventsDatabase().fetchEventCategories()) ?? [];
    },
  },
});
